import { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useNotify, useRefresh, type Identifier } from 'react-admin';

import { updateProjectNumber } from '@/utils/project_number_utils';

type EditProjectNumberDialogProps = {
    open: boolean;
    projectId: Identifier;
    currentProjectNo: number;
    onClose: () => void;
};

export const EditProjectNumberDialog = ({
    open,
    projectId,
    currentProjectNo,
    onClose,
}: EditProjectNumberDialogProps) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [value, setValue] = useState(String(currentProjectNo));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(String(currentProjectNo));
        }
    }, [open, currentProjectNo]);

    const handleSave = async () => {
        const parsed = Number.parseInt(value, 10);
        if (!Number.isFinite(parsed) || parsed < 1) {
            notify('Enter a positive project number', { type: 'warning' });
            return;
        }
        if (parsed === currentProjectNo) {
            notify('Project number is unchanged', { type: 'warning' });
            return;
        }

        setIsSaving(true);
        try {
            await updateProjectNumber(Number(projectId), parsed);
            notify('Project number updated', { type: 'success' });
            refresh();
            onClose();
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Failed to update project number',
                { type: 'error' }
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit project number</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Project number"
                    type="number"
                    fullWidth
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 1, step: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
